/* eslint-disable no-undefined */
import React, { useEffect, useState, useImperativeHandle, useCallback } from "react";
import get from "lodash/get";

//components
import TypesOfLocations from "./TypesOfLocations";
import GenderFilter from "./GenderFilter";
import Checkbox from "@/shared/Checkbox";
import RangeSlider from "@/shared/RangeSlider";
import Collapse from "@/components/Collapse";
import InfoTooltip from "@/components/InfoTooltip";
import SearchFilters from "@/components/SearchComponents/SearchFilters";

//helpers
import { AGE_RANGE_OPTIONS, FEATURED_LIST, checkParamsIsEmpty, TARGET_AUDIENCE_OPTIONS, mapInputNumberProps, WEEKLY_FOOTFALL_MAX_VALUE } from "./helpers";
import { getCurrencySymbol } from "@/helpers/currencyUtils";
import popularSearches from "@/api/query/popularSearches";
import search from "@/api/search";
import analytics from "@/api/analytics";
import useQueryParams from "@/hooks/useQueryParams";
import { buildQueryString } from "@/helpers/searchHelpers";

// interfaces
import { IMoreFiltersForm, TMoreFiltersRef } from "./IMoreFiltersForm";
import { IFacetsBuckets, IFacets } from "@/types/api/Facets";
import { IBuildQueryString } from "@/types/helpers/searchHelpers";

//styles
import styles from "@/assets/styles/components/Form/more-filters-form.module.scss";
import { transformSearchParams } from "@/pageComponents/SearchResultsPageComponent/helpers";
import { debounce } from "lodash";


type TParamsState = Record<string, string | null | boolean>;

interface IHandleCheckChange {
  value: string;
  checked: boolean;
  key: string;
  label: string;
  title: string;
}

interface IOnRangeChange {
  key: string;
  values: number[];
  title: string;
  label: string;
}

const DEFAULT_SEARCH_PARAMS = { ordering: "-high_to_low", offset: 0 };

const MoreFiltersForm = React.forwardRef((props: IMoreFiltersForm, ref: TMoreFiltersRef) => {
  const { currency, facets: initialFacets, setIsShowClear, open, isShowSearchFilter } = props;

  const [params, setParams] = useState<TParamsState>({} as TParamsState);
  const [facets, setFacets] = useState<IFacets | null>(null);
  const [collections, setCollections] = useState<IFacetsBuckets[] | null>(null);
  const [queryParams, setQueryParams] = useQueryParams<TParamsState>();

  const { data: keyCriteria = [] } = popularSearches.useGetSearches();

  const collectionsIn = params.collections__in ? (params.collections__in as string).split("__") : [];
  const currencySymbol = getCurrencySymbol(currency?.code);

  const transformedSearchParams = transformSearchParams(queryParams as Record<string, string | null>);
  const debouncedSetQuery = useCallback(debounce(payload => setQueryParams(payload), 1000), []);

  // methods
  const fetchInitialData = (initParams: TParamsState) => {
    const paramsToUse = {
      ...initParams,
      ...DEFAULT_SEARCH_PARAMS,
    } as unknown as TParamsState;

    search.getResultsCount({
      ...paramsToUse,
      size__in: !!paramsToUse.purpose__in && +paramsToUse.purpose__in === 4 ? null : paramsToUse.size__in,
      type__in: !!paramsToUse.purpose__in && +paramsToUse.purpose__in === 4 ? null : paramsToUse.type__in,
    }).then(data => {
      data && setFacets(data.facets);
      data && setCollections(get(data, "facets.collections.collections.buckets", []));
    });
  };

  const handleClearFilters = () => {
    const currentParams = { ...params };
    [...AGE_RANGE_OPTIONS, ...TARGET_AUDIENCE_OPTIONS].forEach((val) => currentParams[val.key] = null);

    currentParams.popular_searches__in = null;
    currentParams.male__range = null;
    currentParams.female__range = null;
    currentParams.collections__in = null;
    currentParams.rate_daily__range = null;
    currentParams.except_facet = null;
    currentParams.environments = null;
    currentParams.new = null;
    currentParams.trending_locations = null;
    currentParams.iconic_backdrop = null;

    setParams({ ...currentParams });

    fetchInitialData(currentParams);
  };

  const handleSendAnalytics = async ({ title, label, value, minOrMaxChanged, }: { title: string, label: string, value: string, minOrMaxChanged: "min" | "max" | null }) => {
    await analytics.advancedFilterRequest({
      filterTopLevel: title,
      filterSecondLevel: label,
      minOrMaxChanged: minOrMaxChanged || "",
      filterValueSet: value,
    }).catch(() => null);
  };

  const handleCheckChange = async ({ value, checked, key, label, title }: IHandleCheckChange) => {
    const currentQueryValue = (params[key] as string || "").split("__").filter(Boolean);
    const paramsToSet = { ...params, [key]: null, };

    if (!checked) {
      paramsToSet[key] = currentQueryValue.filter(id => id !== `${value}`).join("__");
    } else {
      paramsToSet[key] = [...currentQueryValue, value].join("__");

      if (key === "collections__in") {
        paramsToSet.except_facet = "collection";
      }
    }

    handleSendAnalytics({
      title,
      label,
      value: checked.toString(),
      minOrMaxChanged: null,
    });

    setParams(paramsToSet);
  };

  const onRangeChange = ({ key, values, title, label }: IOnRangeChange) => {
    const setValue = values.join("__");

    setParams(state => {
      const oldValues = state[key] && (state[key] as string)?.split("__").map(Number)

      if (values[0] !== get(oldValues, "[0]", 0)) {
        handleSendAnalytics({
          title,
          label,
          value: values[0].toString(),
          minOrMaxChanged: "min",
        });
      }
  
      if (values[1] !== get(oldValues, "[1]", 0)) {
        handleSendAnalytics({
          title,
          label,
          value: values[1].toString(),
          minOrMaxChanged: "max",
        });
      }

      return ({ ...state, [key]: setValue }) as TParamsState
    });
  };

  const onGenderChange = (key: string, value: string) => {
    if (key === "female__range") {
      setParams((state) => {
        const oldValues = state[key] && (state[key] as string)?.split("__").map(Number);
        const newValue = value.split("__").map(Number);
        
        if (newValue[0] !== get(oldValues, "[0]", 0)) {
          handleSendAnalytics({
            title: "gender",
            label: "Female",
            value: newValue[0].toString(),
            minOrMaxChanged: "min",
          });
        }
    
        if (newValue[1] !== get(oldValues, "[1]", 0)) {
          handleSendAnalytics({
            title: "gender",
            label: "Female",
            value: newValue[1].toString(),
            minOrMaxChanged: "max",
          });
        }

        return { [key]: value, male__range: null }
      });
    }

    if (key === "male__range") {
      setParams((state) => {
        const oldValues = state[key] && (state[key] as string)?.split("__").map(Number);
        const newValue = value.split("__").map(Number);
        
        if (newValue[0] !== get(oldValues, "[0]", 0)) {
          handleSendAnalytics({
            title: "gender",
            label: "Male",
            value: newValue[0].toString(),
            minOrMaxChanged: "min",
          });
        }
    
        if (newValue[1] !== get(oldValues, "[1]", 0)) {
          handleSendAnalytics({
            title: "gender",
            label: "Male",
            value: newValue[1].toString(),
            minOrMaxChanged: "max",
          });
        }

        return { [key]: value, female__range: null }
      });
    }
  };

  const handleCheckFeatured = (value: string, checked: boolean, label: string) => {
    setParams(state => ({ ...state, [value]: checked || null }));

    handleSendAnalytics({
      title: "featured",
      label,
      value: checked.toString(),
      minOrMaxChanged: null,
    });

  }

  const handleSetMaxValueForRange = (key: string, value: number) => {
    if (key !== "weeklyFootfall") {
      return value;
    }

    if (key === "weeklyFootfall" && value > WEEKLY_FOOTFALL_MAX_VALUE) {
      return WEEKLY_FOOTFALL_MAX_VALUE
    }

    return value;
  }

  const onFiltersChange = (key: string, value: unknown) => {
    const updatedSearchParams = { ...transformedSearchParams, [key]: value };

    if (key === "size__in") {
      updatedSearchParams.sizeCustom = null;
    }

    if (key === "city__in" && updatedSearchParams.environments) {
      updatedSearchParams.environments = null;
      updatedSearchParams.except_facet = null;
      updatedSearchParams.collections__in = null;
    }

    const queryParamsPayload = buildQueryString(updatedSearchParams as IBuildQueryString).object;

    if (key === "sizeCustom") {
      debouncedSetQuery(queryParamsPayload);

      return;
    }

    setQueryParams(queryParamsPayload);
  };


  const getPopularSearchesChecked = (id: string) => (params.popular_searches__in as string || "").split("__").includes(`${id}`);
  const getRangeValues = (el: { key: string }) => params[el.key] && (params[el.key] as string)?.split("__").map(Number);

  // effects
  useImperativeHandle(ref, () => ({ clearFilters: handleClearFilters }));

  useEffect(() => {
    if (!open) {
      return;
    }

    checkParamsIsEmpty({ params, setIsShowClear });
  }, [queryParams, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setParams(queryParams);
  }, [
    queryParams.city__in,
    queryParams.start_date,
    queryParams.end_date,
    queryParams.purpose__in,
    queryParams.size__in,
    queryParams.width__gte,
    queryParams.length__gte,
    queryParams.type__in,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (JSON.stringify(params) !== JSON.stringify(queryParams)) {
      setParams(queryParams)
    }
  }, [
    queryParams.collections__in,
    queryParams.new,
    queryParams.trending_locations,
    queryParams.iconic_backdrop,
    queryParams.popular_searches__in,
    queryParams.rate_daily__range,
    queryParams.gen_z__range,
    queryParams.gen_y__range,
    queryParams.gen_x__range,
    queryParams.baby_boomer__range,
    queryParams.weekly_footfall__range,
    queryParams.spending__range,
    queryParams.dwell_time__range,
    queryParams.abc1__range,
    queryParams.female__range,
    queryParams.male__range,
  ]);

  useEffect(() => {
    setQueryParams({ ...params, page: null });
  }, [params]);

  useEffect(() => {
    setParams(queryParams);
  }, []);

  useEffect(() => {
    if (initialFacets && !facets) {
      setFacets(initialFacets);
    }

    if (!collections) {
      setCollections(get(initialFacets, "collections.collections.buckets", []));
    }
  }, []);

  useEffect(() => {
    fetchInitialData({
      ...queryParams,
      size__in: queryParams.size__in === "custom" ? null : queryParams.size__in,
    });
  }, [
    queryParams.city__in,
    queryParams.start_date,
    queryParams.end_date,
    queryParams.purpose__in,
    queryParams.size__in,
    queryParams.width__gte,
    queryParams.length__gte,
    queryParams.type__in,
  ]);

  // render
  const renderCollapseAsNew = (title: string) => (
    <div className={styles["locl-more-filters__panel__title"]}>{title} <div className={styles["locl-more-filters__panel__badge-new"]}>NEW</div></div>
  )

  const SEARCH_FILTERS = [
    process.env.NEXT_PUBLIC_APP_WL_CODE === "LOCL" && {
      key: "1",
      title: "Environment",
      content: (
        <TypesOfLocations
          onClick={(value, checked, label) => handleCheckChange({
            value,
            checked,
            key: "collections__in",
            label: label,
            title: "environment",
          })}
          values={collectionsIn}
        />
      ),
    },
    process.env.NEXT_PUBLIC_APP_WL_CODE === "LOCL" && {
      key: "2",
      title: renderCollapseAsNew("Featured"),
      content: (
        <div className={styles["locl-more-filters__checkboxes__wrap"]}>
          {
            FEATURED_LIST?.map((el, index) => (
              <Checkbox
                checked={!!get(params, (el as { value: string, label: string }).value, false)}
                onChange={(e) => handleCheckFeatured(
                  el.value,
                  e.target.checked,
                  el.label
                )}
                data-testid="locl-more-filters-feature"
                key={`locl-more-filters-${(el as { value: string, label: string }).label}-${index}`}
                size="small"
              >
                {el.label}
              </Checkbox>
            ))
          }
        </div>
      ),
    },
    {
      key: "3",
      title: "Space requirements",
      content: (
        <div className={styles["locl-more-filters__checkboxes__wrap"]}>
          {
            keyCriteria?.map((el, index) => (
              <Checkbox
                checked={getPopularSearchesChecked(`${el.id}`)}
                onChange={(e) => handleCheckChange({
                  value: `${el.id}`,
                  checked: e.target.checked,
                  key: "popular_searches__in",
                  title: "space_requirements",
                  label: el.name,
                })}
                wrapperClassName="locl-more-filters__key-criteria__checkbox"
                data-testid="locl-more-filters-key-criteria"
                key={`locl-more-filters-${el.name}-${index}`}
                size="small"
              >
                {el.name}
              </Checkbox>
            ))
          }
        </div>
      ),
    },
    {
      key: "4",
      title: "Budget range",
      content: (
        <RangeSlider
          value={getRangeValues({ key: "rate_daily__range" }) as unknown as number[]}
          withInputs
          label="Price per day"
          min={get(facets as IFacets, "rateDaily.rateDaily.min.value")}
          max={get(facets as IFacets, "rateDaily.rateDaily.max.value")}
          onChange={values => onRangeChange({
            key: "rate_daily__range",
            values,
            title: "budget_range",
            label: "Price per day",
          })}
          wrapperClassName={styles["locl-more-filters__input-wrap"]}
          inputDataTest="locl-more-filters-budget-input"
          { ...mapInputNumberProps("rate_daily__range", currencySymbol) }
        />
      ),
    },
    {
      key: "5",
      title: "Target audience",
      content: (
        <div className={styles["locl-more-filters__target-audience"]}>
          {TARGET_AUDIENCE_OPTIONS.map((el) => (
            <RangeSlider
              withInputs
              label={el.title}
              value={getRangeValues(el) as unknown as number[]}
              min={handleSetMaxValueForRange(el.apiKey, get(facets, `${el.apiKey}.min.value`, 0))}
              max={handleSetMaxValueForRange(el.apiKey, get(facets, `${el.apiKey}.max.value`, 0))}
              onChange={values => onRangeChange({
                key: el.key,
                values,
                title: "target_audience",
                label: el.title,
              })}
              key={`range-slider-${el.key}`}
              wrapperClassName={styles["locl-more-filters__input-wrap"]}
              inputDataTest={`locl-more-filters-input-${el.key}`}
              { ...mapInputNumberProps(el.apiKey, currencySymbol) }
            />))
          }
        </div>
      ),
    },
    {
      key: "6",
      title: "Age",
      content: (
        <div className="locl-more-filters__age">
          {AGE_RANGE_OPTIONS.map((el) => (
            <RangeSlider
              withInputs
              label={el.title}
              onChange={values => onRangeChange({
                key: el.key,
                values,
                title: "age",
                label: el.title,
              })}
              value={getRangeValues(el) as unknown as number[]}
              min={get(facets, `${el.apiKey}.min.value`, 0)}
              max={get(facets, `${el.apiKey}.max.value`, 0)}
              key={`range-slider-${el.key}`}
              className="locl-more-filters__age_input"
              wrapperClassName={styles["locl-more-filters__input-wrap"]}
              inputDataTest={`locl-more-filters-input-${el.key}`}
              { ...mapInputNumberProps(el.apiKey, currencySymbol) }
            />)
          )}
        </div>
      )
    },
    {
      key: "7",
      title: (<>Gender <InfoTooltip title="Set the percentage range for the selected gender" overlayClassName={styles["locl-more-filters__tooltip"]} /></>),
      content: (
        <GenderFilter
          onChange={onGenderChange}
          femaleValue={getRangeValues({ key: "female__range" }) as unknown as number[]}
          maleValue={getRangeValues({ key: "male__range" }) as unknown as number[]}
        />
      ),
    }
  ].filter(Boolean) as { key: string, title: string, content: JSX.Element }[];

  return (
    <div
      className={styles["locl-more-filters"]}
      data-testid="locl-more-filters"
    >
      <div className={styles["locl-more-filters__content"]} key={`locl-more-filters__content-${currencySymbol}`}>
        {
          isShowSearchFilter && (
            <div className={styles["locl-more-filters__main-filter"]}>
              <SearchFilters
                value={transformedSearchParams}
                onChange={onFiltersChange}
                showSearch={false}
                onSearch={() => ({})}
                hideFilterKeys={["city__in", "date__in"]}
              />
            </div>
          )
        }
        <Collapse
          defaultActiveKey={["1", "2", "3", "4", "5", "6", "7"]}
        >
          {
            SEARCH_FILTERS.map(({ key, title, content }) => (
              <Collapse.Panel
                header={title}
                key={key}
                className={styles["locl-more-filters__panel"]}
                showArrow
              >
                {content}
              </Collapse.Panel>
            ))
          
          }
        </Collapse>
      </div>
    </div>
  );
});


MoreFiltersForm.displayName = "MoreFiltersForm";

export default MoreFiltersForm;
